import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  downloadOrdenCompraDetalleCsvByCodigo,
  fetchOrdenCompra,
  fetchOrdenCompraConsultaByCodigo,
} from '../api/ordenes_compra';
import { useLoading } from '../contexts/LoadingContext';
import { useToast } from '../contexts/ToastContext';
import { formatDateSV } from '../utils/date';
import { generarOrdenCompraPDF } from '../utils/generarOrdenCompraPDF';

const currency = (value) => `$ ${Number(value || 0).toFixed(2)}`;

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function buildFallbackResumen(orden) {
  return {
    codigo: orden?.codigo ?? '',
    total_guardado: Number(orden?.total_orden || 0),
    total_productos: 0,
    total_libraje_quemado: 0,
    total_real: 0,
  };
}

export default function OrdenCompraDetalle() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { start, stop } = useLoading();
  const { showToast } = useToast();

  const codigoParam = String(searchParams.get('codigo') || '').trim();

  const [codigoBusqueda, setCodigoBusqueda] = useState(codigoParam);
  const [skuBusqueda, setSkuBusqueda] = useState('');
  const [orden, setOrden] = useState(null);
  const [loading, setLoading] = useState(Boolean(id || codigoParam));
  const [error, setError] = useState('');

  useEffect(() => {
    stop();
  }, [stop]);

  useEffect(() => {
    setCodigoBusqueda(codigoParam);
  }, [codigoParam]);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      if (!id && !codigoParam) {
        setOrden(null);
        setError('');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const payload = id
          ? await fetchOrdenCompra(id)
          : await fetchOrdenCompraConsultaByCodigo(codigoParam);

        const data = payload?.data ?? payload ?? null;
        if (ignore) return;

        setOrden(data);
        setCodigoBusqueda(data?.codigo ?? codigoParam);
      } catch (err) {
        if (ignore) return;
        const message = err?.response?.data?.error || 'No se encontro la orden.';
        setOrden(null);
        setError(message);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [codigoParam, id]);

  const resumen = useMemo(
    () => orden?.resumen ?? buildFallbackResumen(orden),
    [orden]
  );

  const lineasFiltradas = useMemo(() => {
    const sku = skuBusqueda.trim().toLowerCase();
    const lineas = orden?.lineas || [];
    if (!sku) return lineas;

    return lineas.filter((linea) =>
      String(linea.sku || linea.producto?.sku || '').toLowerCase().includes(sku)
    );
  }, [orden, skuBusqueda]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const codigo = codigoBusqueda.trim();

    if (!codigo) {
      showToast({ message: 'Ingresa un codigo de orden.', severity: 'warning' });
      return;
    }

    nav(`/ordenes_compra/consulta?codigo=${encodeURIComponent(codigo)}`);
  };

  const handleClear = () => {
    setCodigoBusqueda('');
    setSkuBusqueda('');
    setError('');
    setOrden(null);
    nav('/ordenes_compra/consulta', { replace: true });
  };

  const handleDownloadCsv = async () => {
    if (!orden?.codigo) return;

    try {
      const blob = await downloadOrdenCompraDetalleCsvByCodigo(orden.codigo);
      triggerDownload(blob, `orden_compra_${orden.codigo}.csv`);
    } catch (err) {
      console.error('Error descargando CSV de orden de compra', err);
      showToast({ message: 'No se pudo descargar el CSV.', severity: 'error' });
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        mt: 2,
        mb: 0,
        pb: 4,
        height: 'calc(100vh - 100px)',
        overflowY: 'auto',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        mb={2}
      >
        <Typography variant="h5">
          {orden ? `Detalle Orden #${orden.id} - ${orden.codigo}` : 'Consulta por orden de compra'}
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => {
              start();
              nav('/ordenes_compra');
            }}
          >
            Volver
          </Button>
          <Button
            variant="outlined"
            onClick={handleDownloadCsv}
            disabled={!orden?.codigo}
          >
            Descargar CSV
          </Button>
          <Button
            variant="contained"
            onClick={() => generarOrdenCompraPDF(orden)}
            disabled={!orden}
          >
            Descargar PDF
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <TextField
            label="Codigo OC"
            size="small"
            value={codigoBusqueda}
            onChange={(event) => setCodigoBusqueda(event.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 280 } }}
          />
          <TextField
            label="Buscar por SKU"
            size="small"
            value={skuBusqueda}
            onChange={(event) => setSkuBusqueda(event.target.value)}
            disabled={!orden || loading}
            sx={{ minWidth: { xs: '100%', sm: 220 } }}
          />
          <Button type="submit" variant="contained" disabled={loading}>
            Consultar
          </Button>
          <Button
            type="button"
            variant="text"
            disabled={loading || (!codigoBusqueda && !orden)}
            onClick={handleClear}
          >
            Limpiar
          </Button>
        </Box>
      </Paper>

      {loading && (
        <Container sx={{ mt: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Container>
      )}

      {!loading && error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !orden && !error && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Consulta una orden por codigo
          </Typography>
          <Typography color="text.secondary">
            Ingresa el codigo de la orden para ver su resumen, detalle y exportacion CSV.
          </Typography>
        </Paper>
      )}

      {!loading && orden && (
        <>
          <Divider sx={{ mb: 3 }} />

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography>
                    <strong>Proveedor:</strong> {orden.proveedor_nombre || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography>
                    <strong>Fecha:</strong> {orden.fecha ? formatDateSV(orden.fecha) : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography>
                    <strong>Estado:</strong> {orden.estado || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography>
                    <strong>Total guardado:</strong> {currency(resumen.total_guardado)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography>
                    <strong>Codigo:</strong> {orden.codigo}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total productos
                  </Typography>
                  <Typography variant="h6">{currency(resumen.total_productos)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total libraje quemado
                  </Typography>
                  <Typography variant="h6">{currency(resumen.total_libraje_quemado)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total real
                  </Typography>
                  <Typography variant="h6">{currency(resumen.total_real)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Lineas
                  </Typography>
                  <Typography variant="h6">{orden.lineas?.length || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom>
            Lineas de detalle
          </Typography>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>Imagen</strong></TableCell>
                  <TableCell><strong>Producto</strong></TableCell>
                  <TableCell align="right"><strong>Cant.</strong></TableCell>
                  <TableCell align="right"><strong>Precio compra/u</strong></TableCell>
                  <TableCell align="right"><strong>Subtotal</strong></TableCell>
                  <TableCell align="right"><strong>Libraje quemado</strong></TableCell>
                  <TableCell align="right"><strong>Libraje producto</strong></TableCell>
                  <TableCell align="right"><strong>Libraje usado</strong></TableCell>
                  <TableCell align="right"><strong>Libraje linea</strong></TableCell>
                  <TableCell align="right"><strong>Total linea</strong></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {lineasFiltradas.map((linea) => (
                  <TableRow key={linea.detalle_id || linea.id}>
                    <TableCell>{linea.detalle_id || linea.id}</TableCell>

                    <TableCell width={80}>
                      {linea.producto?.imagen_url ? (
                        <Box
                          component="img"
                          src={linea.producto.imagen_url}
                          alt={linea.nombre || linea.producto?.nombre || 'Producto'}
                          loading="lazy"
                          sx={{
                            width: 56,
                            height: 56,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Sin imagen
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>{linea.nombre || linea.producto?.nombre || `ID ${linea.producto_id}`}</TableCell>
                    <TableCell align="right">{linea.cantidad}</TableCell>
                    <TableCell align="right">{currency(linea.precio_unitario)}</TableCell>
                    <TableCell align="right">{currency(linea.subtotal)}</TableCell>
                    <TableCell align="right">{currency(linea.libraje_quemado)}</TableCell>
                    <TableCell align="right">{currency(linea.libraje_producto)}</TableCell>
                    <TableCell align="right">{currency(linea.libraje_usado)}</TableCell>
                    <TableCell align="right">{currency(linea.libraje_linea)}</TableCell>
                    <TableCell align="right">{currency(linea.total_linea)}</TableCell>
                  </TableRow>
                ))}

                {lineasFiltradas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      {skuBusqueda.trim()
                        ? 'No hay productos con ese SKU en la orden.'
                        : 'La orden existe pero no tiene lineas de detalle.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Container>
  );
}
